import Grid from '@mui/material/Grid';
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Loading from '../components/layouts/Loading';
import { CartContext } from '../contexts/CartContext';
import { showToast } from '../redux/actions';
import { getProducts } from '../redux/actions/products';
import { products$, toast$ } from '../redux/selectors';

const Tooltip = lazy(() => import('../components/layouts/Tooltip'));

const Cart = () => {
  const {
    cartState: { cart },
    updateCart,
    deleteCartItem,
    deleteCart,
  } = useContext(CartContext);
  const [total, setTotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const products = useSelector(products$);
  const toast = useSelector(toast$);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    if (products.data.length === 0) {
      dispatch(getProducts.getProductsRequest());
    }
  }, [dispatch, products.data.length]);

  const onDeleteCartItem = useCallback(
    (product) => {
      deleteCartItem(product);
    },
    [deleteCartItem]
  );

  const onDeleteCart = useCallback(() => {
    deleteCart();
    setTotal(0);
    setDiscount(0);
  }, [deleteCart]);

  useEffect(() => {
    if (cart) {
      let sumTotal = 0;
      let sumDiscount = 0;
      cart.forEach((item, index) => {
        sumTotal += item.total;
        sumDiscount += item.product.discount * item.quantity;
        const product = products.data.find(
          (dataItem) => dataItem._id === item?.product?.id
        );
        if (parseInt(item.quantity) > parseInt(product?.quantity)) {
          updateCart(index, product.quantity, product);
        }
      });
      setTotal(sumTotal);
      setDiscount(sumDiscount);
    }
  }, [cart, products.data, updateCart]);

  const onClickDec = useCallback(
    (index, quantity) => {
      const product = products.data.find(
        (dataItem) => dataItem._id === cart[index]?.product?.id
      );
      if (quantity > 1) {
        cart[index].quantity -= 1;
      } else {
        cart[index].quantity = 1;
      }
      updateCart(index, cart[index].quantity, product);
    },
    [cart, products.data, updateCart]
  );

  const onClickInc = useCallback(
    (index) => {
      const product = products.data.find(
        (dataItem) => dataItem._id === cart[index]?.product?.id
      );
      updateCart(index, cart[index].quantity + 1, product);
    },
    [cart, products.data, updateCart]
  );

  return (
    <>
      <Suspense fallback={<Loading />}>
        <Tooltip toast={toast} />
      </Suspense>
      {/* <!-- Breadcrumb Section Begin --> */}
      <section
        className="breadcrumb-section set-bg"
        style={{
          backgroundImage: 'url(img/breadcrumb.jpg)',
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-lg-12 text-center">
              <div className="breadcrumb__text">
                <h2>{t('header.cart')}</h2>
                <div className="breadcrumb__option">
                  <Link to="/">{t('header.home')}</Link>
                  <span>{t('cart.title')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* <!-- Breadcrumb Section End -->

  <!-- Shoping Cart Section Begin --> */}
      <section className="shoping-cart spad">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="shoping__cart__table">
                <table>
                  <thead>
                    <tr>
                      <th className="shoping__product">{t('cart.product')}</th>
                      <th>{t('cart.price')}</th>
                      <th>{t('cart.discount')}</th>
                      <th>{t('cart.quantity')}</th>
                      <th>{t('cart.money')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cart &&
                      cart.map((item, index) => (
                        <tr key={item?.product?.id}>
                          <td className="shoping__cart__item">
                            <Grid container>
                              <Grid item xs={12} sm={4} md={4}>
                                {/* <img
                                  src={item?.product?.images[0].imageUrl}
                                  alt=""
                                  width="150"
                                /> */}
                                <LazyLoadImage
                                  effect="blur"
                                  src={item?.product?.images[0].imageUrl}
                                  alt="product"
                                  width="150"
                                  placeholderSrc="img/logo.png"
                                />
                              </Grid>
                              <Grid item xs={12} sm={8} md={8}>
                                <h5 style={{ display: 'inline-grid' }}>
                                  {item?.product?.name}
                                </h5>
                                <br />
                                <small>
                                  {item?.product?.sizes.length > 0 &&
                                    item?.product?.sizes[0] !== null &&
                                    `Kích thước: ${item?.product?.sizes[0]}`}
                                  {item?.product?.colors.length > 0 &&
                                    item?.product?.colors[0] !== null &&
                                    `, Màu sắc: ${item?.product?.colors[0]}`}
                                </small>
                              </Grid>
                            </Grid>
                          </td>
                          <td className="shoping__cart__price">
                            {String(item?.product?.price).replace(
                              /(.)(?=(\d{3})+$)/g,
                              '$1,'
                            )}{' '}
                            &#8363;
                          </td>
                          <td className="shoping__cart__price">
                            {String(item?.product?.discount).replace(
                              /(.)(?=(\d{3})+$)/g,
                              '$1,'
                            )}{' '}
                            &#8363;
                          </td>
                          <td className="shoping__cart__quantity">
                            <div className="quantity">
                              <div className="pro-qty">
                                <span
                                  className="dec qtybtn"
                                  onClick={() =>
                                    onClickDec(index, item?.quantity)
                                  }
                                >
                                  -
                                </span>
                                <input
                                  type="text"
                                  value={item?.quantity}
                                  readOnly
                                />
                                <span
                                  className="inc qtybtn"
                                  onClick={() => onClickInc(index)}
                                >
                                  +
                                </span>
                              </div>
                            </div>
                            <div>
                              {t('cart.remain')}:{' '}
                              {
                                products.data.find(
                                  (dataItem) =>
                                    dataItem._id === item?.product?.id
                                )?.quantity
                              }
                            </div>
                          </td>
                          <td className="shoping__cart__total">
                            {String(item?.total).replace(
                              /(.)(?=(\d{3})+$)/g,
                              '$1,'
                            )}{' '}
                            &#8363;
                          </td>
                          <td className="shoping__cart__item__close">
                            <span
                              className="icon_close"
                              onClick={() => {
                                onDeleteCartItem(item?.product);
                                dispatch(
                                  showToast({
                                    message: t('cart.deleteCartItem'),
                                    type: 'info',
                                  })
                                );
                              }}
                            ></span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12">
              <div className="shoping__cart__btns">
                <Link to="/" className="primary-btn cart-btn">
                  {t('cart.shoppingBtn')}
                </Link>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                  onClick={onDeleteCart}
                  className="primary-btn cart-btn cart-btn-right"
                >
                  <span className="icon_loading"></span>
                  {t('cart.deleteBtn')}
                </a>
              </div>
            </div>
            <div className="col-lg-6">
              {/* <div className="shoping__continue">
                <div className="shoping__discount">
                  <h5>Mã giảm giá</h5>
                  <form action="#">
                    <input type="text" placeholder="Nhập mã giảm giá" />
                    <button type="submit" className="site-btn">
                      ÁP DỤNG
                    </button>
                  </form>
                </div>
              </div> */}
            </div>
            <div className="col-lg-6">
              <div className="shoping__checkout">
                <h5>{t('cart.totalMoney')}</h5>
                <ul>
                  <li>
                    {t('cart.discounted')}{' '}
                    <span>
                      {String(discount).replace(/(.)(?=(\d{3})+$)/g, '$1,')}{' '}
                      &#8363;
                    </span>
                  </li>
                  <li>
                    {t('cart.pay')}{' '}
                    <span>
                      {String(total).replace(/(.)(?=(\d{3})+$)/g, '$1,')}{' '}
                      &#8363;
                    </span>
                  </li>
                </ul>
                {total !== 0 ? (
                  <Link to="/checkout" className="primary-btn">
                    {t('cart.order')}
                  </Link>
                ) : (
                  <Link to="/products" className="primary-btn">
                    {t('cart.emptyOrder')}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* <!-- Shoping Cart Section End --> */}
    </>
  );
};

export default memo(Cart);
